import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Avatar from "./Avatar";
import { Trash2, Share2, Target } from "lucide-react";
import { toast } from "sonner";
import PlaceMedal from './PlaceMedal';

// Compact share buttons for table rows
const ShareButtonsCompact = ({ gameId, userId, gameName, accuracy, t }) => {
  const shareUrl = `https://countryscanner.ru/game/${gameId}/user-results/${userId}`;
  const shareText = t ? t("results.shareText", { game: gameName, accuracy }) : `My result in "${gameName}": ${accuracy}%!`;

  const handleShare = (platform, e) => {
    e.stopPropagation();
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    
    switch (platform) {
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%0A${encodedUrl}`, '_blank');
        break;
      case 'vk':
        window.open(`https://vk.com/share.php?url=${encodedUrl}&title=${encodedText}`, '_blank');
        break;
      case 'ok':
        window.open(`https://connect.ok.ru/offer?url=${encodedUrl}&title=${encodedText}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl).then(() => {
          toast.success(t("toast.linkCopied"));
        }).catch(() => {
          toast.error(t("toast.copyFailed"));
        });
        break;
    }
  };

  const btnStyle = {
    padding: '4px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s'
  };

  return (
    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
      <button onClick={(e) => handleShare('telegram', e)} style={{ ...btnStyle, background: '#0088cc' }} title="Telegram">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('whatsapp', e)} style={{ ...btnStyle, background: '#25D366' }} title="WhatsApp">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('vk', e)} style={{ ...btnStyle, background: '#0077FF' }} title="VK">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.49 4 8.08c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.847 2.455 2.27 4.607 2.86 4.607.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.372 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.253-1.406 2.15-3.574 2.15-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('ok', e)} style={{ ...btnStyle, background: '#EE8208' }} title="OK">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 4.8a4.2 4.2 0 1 1 0 8.4 4.2 4.2 0 0 1 0-8.4zm0 6.6a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8zm4.789 2.29a7.2 7.2 0 0 1-9.578 0 .9.9 0 0 0-1.211 1.33c.9.82 1.953 1.448 3.1 1.856l-2.648 2.647a.9.9 0 1 0 1.273 1.273L12 16.522l4.275 4.274a.9.9 0 0 0 1.273-1.273l-2.648-2.647a8.085 8.085 0 0 0 3.1-1.856.9.9 0 0 0-1.211-1.33z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('copy', e)} style={{ ...btnStyle, background: 'linear-gradient(135deg, #7B68EE, #9370DB)' }} title="Max (скопировать)">
        <svg width="14" height="14" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="white"/>
          <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#7B68EE" fontFamily="Arial">M</text>
        </svg>
      </button>
    </div>
  );
};

const ResultsTable = ({ 
  gameId, 
  results, 
  winners, 
  game,
  currentUserId = null,
  currentUser = null,
  showPrizePlaceholders = true,
  itemsPerPage = 15,
  onDeleteResult = null
}) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [displayCount, setDisplayCount] = useState(itemsPerPage);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // Check if current user is admin or editor
  const canDelete = currentUser && (currentUser.role === 'admin' || currentUser.role === 'editor');
  
  const handleDelete = async (userId, userName) => {
    if (!onDeleteResult) return;
    
    const confirmText = t('results.confirmDelete', { name: userName });
    const confirmed = window.confirm(confirmText);
    
    if (!confirmed) return;
    
    setDeletingUserId(userId);
    try {
      await onDeleteResult(userId);
    } finally {
      setDeletingUserId(null);
    }
  };

  // Get winner config with defaults
  const winnerConfig = game?.winner_config || {};
  const firstThreshold = winnerConfig.first_place_threshold || 95;
  const secondThreshold = winnerConfig.second_place_threshold || 85;
  const thirdThreshold = winnerConfig.third_place_threshold || 75;

  // Calculate accuracy for all results
  const resultsWithAccuracy = results.map(result => {
    const maxPossibleScore = result.total_panoramas * 5000;
    const accuracyPercent = maxPossibleScore > 0 
      ? ((result.total_score / maxPossibleScore) * 100)
      : 0;
    
    return { ...result, accuracyPercent };
  });

  // Use winners from API
  const firstPlace = winners.find(w => w.place === 1) 
    ? resultsWithAccuracy.find(r => r.user_id === winners.find(w => w.place === 1)?.user_id)
    : null;
  const secondPlace = winners.find(w => w.place === 2)
    ? resultsWithAccuracy.find(r => r.user_id === winners.find(w => w.place === 2)?.user_id)
    : null;
  const thirdPlace = winners.find(w => w.place === 3)
    ? resultsWithAccuracy.find(r => r.user_id === winners.find(w => w.place === 3)?.user_id)
    : null;

  // Other players (everyone except the 3 podium winners)
  // For objects_only: sort by found_hotspots desc, then by last activity
  // For normal games: sort by score descending
  const otherPlayers = resultsWithAccuracy
    .filter(r => r !== firstPlace && r !== secondPlace && r !== thirdPlace)
    .sort((a, b) => {
      if (game?.game_mode === 'objects_only') {
        // Sort by found hotspots (desc), then by total_score as tiebreaker
        const hotspotsA = a.found_hotspots || 0;
        const hotspotsB = b.found_hotspots || 0;
        if (hotspotsB !== hotspotsA) return hotspotsB - hotspotsA;
        return b.total_score - a.total_score;
      }
      return b.total_score - a.total_score;
    });

  // Build display array
  const displayRows = [];
  
  // For objects_only mode, skip prize places entirely - just show all players sorted by hotspots found
  if (game?.game_mode !== 'objects_only') {
    // 1st place
    displayRows.push({ 
      place: 1, 
      result: firstPlace, 
      threshold: firstThreshold, 
      medal: firstPlace ? <PlaceMedal place={1} size="md" /> : null,
      note: null 
    });
    
    // 2nd place
    displayRows.push({ 
      place: 2, 
      result: secondPlace, 
      threshold: secondThreshold, 
      medal: secondPlace ? <PlaceMedal place={2} size="md" /> : null,
      note: null 
    });
    
    // 3rd place
    displayRows.push({ 
      place: 3, 
      result: thirdPlace, 
      threshold: thirdThreshold, 
      medal: thirdPlace ? <PlaceMedal place={3} size="md" /> : null,
      note: null 
    });
  }

  // Others - add notes for those who reached thresholds
  otherPlayers.forEach(result => {
    let note = null;
    const accuracy = result.accuracyPercent;
    
    // Check which threshold they reached
    if (accuracy >= firstThreshold && firstPlace) {
      note = t('results.reachedThreshold1').replace('{threshold}', firstThreshold);
    } else if (accuracy >= secondThreshold && secondPlace) {
      note = t('results.reachedThreshold2').replace('{threshold}', secondThreshold);
    } else if (accuracy >= thirdThreshold && thirdPlace) {
      note = t('results.reachedThreshold3').replace('{threshold}', thirdThreshold);
    }
    
    displayRows.push({ 
      place: null, 
      result, 
      threshold: null, 
      medal: null,
      note 
    });
  });

  // Limit display
  const visibleRows = displayRows.slice(0, displayCount);
  const hasMore = displayCount < displayRows.length;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: '-', time: '' };
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return { date: `${day}.${month}.${year}`, time: `${hours}:${minutes}` };
    } catch (e) {
      return { date: '-', time: '' };
    }
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + itemsPerPage);
  };

  return (
    <div>
      {/* Single responsive table view (no separate mobile layout) */}
      <div className="results-table-desktop">
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th style={{ width: game?.game_mode === 'objects_only' ? '40%' : (canDelete ? '30%' : '35%') }}>{t("results.player")}</th>
                {game?.game_mode !== 'objects_only' && <th style={{ width: '15%' }}>{t("results.answersAccuracy")}</th>}
                <th style={{ width: game?.game_mode === 'objects_only' ? '30%' : '12%' }}>{t("game.objects")}</th>
                {game?.game_mode !== 'objects_only' && <th style={{ width: '15%' }}>{t("results.score")}</th>}
                <th style={{ width: game?.game_mode === 'objects_only' ? '30%' : (canDelete ? '13%' : '18%') }}>{t("results.dateTime")}</th>
                {canDelete && <th style={{ width: '10%' }}>{t("results.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => {
                const { place, result, threshold, medal, note } = row;
                
                // If no result (vacant place)
                if (!result) {
                  return (
                    <tr key={`vacant-${place}`} style={{ 
                      background: 'rgba(251, 191, 36, 0.05)',
                      border: '1px dashed #fbbf24'
                    }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>{medal}</span>
                          <div style={{ 
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            color: '#9ca3af'
                          }}>
                            ?
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#9ca3af', fontSize: '1rem' }}>
                              {t("results.placeNotTaken")}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                              {t("results.requiredAccuracy", { threshold })}
                            </div>
                          </div>
                        </div>
                      </td>
                      {game?.game_mode !== 'objects_only' && <td style={{ color: '#9ca3af', fontSize: '0.9rem' }}>≥ {threshold}%</td>}
                      <td style={{ color: '#9ca3af' }}>—</td>
                      <td style={{ color: '#9ca3af' }}>—</td>
                      {canDelete && <td style={{ color: '#9ca3af' }}>—</td>}
                    </tr>
                  );
                }
                
                // Player with result
                const maxPossibleScore = result.total_panoramas * 5000;
                const accuracyPercent = result.accuracyPercent.toFixed(1);
                const isCurrentUser = result.user_id === currentUserId;
                const isWinner = !!medal;
                
                // Get date/time
                const winner = winners.find(w => w.user_id === result.user_id);
                let dateTime = null;
                
                if (winner && winner.achieved_at) {
                  dateTime = winner.achieved_at;
                } else if (result.answers && result.answers.length > 0) {
                  const timestamps = result.answers
                    .map(a => a.guessed_at)
                    .filter(t => t)
                    .sort()
                    .reverse();
                  if (timestamps.length > 0) {
                    dateTime = timestamps[0];
                  }
                } else if (result.last_updated) {
                  dateTime = result.last_updated;
                }
                
                return (
                  <tr key={result.id} style={{ 
                    background: isCurrentUser ? 'rgba(102, 126, 234, 0.1)' : place ? 'rgba(251, 191, 36, 0.05)' : 'transparent',
                    position: 'relative'
                  }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative' }}>
                        {medal && (
                          <div style={{
                            position: 'absolute',
                            left: '-12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '1.5rem',
                            zIndex: 1
                          }}>
                            {medal}
                          </div>
                        )}
                        <Avatar
                          userId={result.user_id}
                          avatarUrl={result.avatar_url}
                          userName={result.user_full_name}
                          userEmail={result.user_email}
                          size={32}
                          country={result.country}
                          clickable={false}
                        />
                        <div>
                          <div>
                            <span 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/${gameId}/user-results/${result.user_id}`);
                              }}
                              style={{ 
                                color: '#667eea',
                                cursor: 'pointer',
                                fontWeight: 500,
                                textDecoration: 'underline'
                              }}
                            >
                              {result.user_full_name}
                            </span>
                            {isCurrentUser && (
                              <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#667eea' }}>
                                ({t("results.you")})
                              </span>
                            )}
                          </div>
                          {/* Share buttons for current user */}
                          {isCurrentUser && (
                            <ShareButtonsCompact 
                              gameId={gameId}
                              userId={result.user_id}
                              gameName={game?.name || ''}
                              accuracy={accuracyPercent}
                              t={t}
                            />
                          )}
                          {note && (
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: '#9ca3af',
                              marginTop: '0.25rem',
                              fontStyle: 'italic'
                            }}>
                              {note}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {game?.game_mode !== 'objects_only' && (
                    <td>
                      <div>
                        <span style={{ fontWeight: 600, color: isWinner ? '#fbbf24' : '#667eea', fontSize: '1.05rem' }}>
                          {accuracyPercent}%
                        </span>
                        <span style={{ color: '#718096', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                          ({t('results.answersOfTotal', { count: result.answered_count, total: result.total_panoramas })})
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div style={{ marginTop: '0.4rem', width: '100%', maxWidth: '120px' }}>
                        <div style={{
                          height: '6px',
                          backgroundColor: result.answered_count < result.total_panoramas ? '#ef4444' : '#e5e7eb',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            height: '100%',
                            width: result.total_panoramas ? ((result.answered_count / result.total_panoramas) * 100) + '%' : '0%',
                            backgroundColor: '#10b981',
                            borderRadius: '3px'
                          }} />
                        </div>
                      </div>
                      {isWinner && threshold && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                          {t("results.requiredThreshold", { threshold })}
                        </div>
                      )}
                    </td>
                    )}
                    <td>
                      {result.total_hotspots > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Target size={16} style={{ color: result.found_hotspots > 0 ? '#8b5cf6' : '#9ca3af' }} />
                          <span style={{ 
                            fontWeight: 600, 
                            color: result.found_hotspots > 0 ? '#8b5cf6' : '#9ca3af'
                          }}>
                            {result.found_hotspots || 0}/{result.total_hotspots}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    {game?.game_mode !== 'objects_only' && (
                    <td>
                      <span style={{ fontWeight: 600, color: isWinner ? '#fbbf24' : '#667eea', fontSize: '1.05rem' }}>
                        {result.total_score.toLocaleString('ru-RU')}
                      </span>
                      <span style={{ color: '#718096', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                        {t('results.ofMaxScore', { max: maxPossibleScore.toLocaleString(language === 'ru' ? 'ru-RU' : 'en-US') })}
                      </span>
                    </td>
                    )}
                    <td className="results-date" style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                      {(() => {
                        const dt = formatDateTime(dateTime);
                        return (
                          <span style={{ display: 'inline-block', lineHeight: 1.3 }}>
                            <span style={{ whiteSpace: 'nowrap' }}>{dt.date}</span>
                            {dt.time && <> <span style={{ whiteSpace: 'nowrap' }}>{dt.time}</span></>}
                          </span>
                        );
                      })()}
                    </td>
                    {canDelete && (
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(result.user_id, result.user_full_name);
                          }}
                          disabled={deletingUserId === result.user_id}
                          style={{
                            padding: '0.5rem',
                            background: deletingUserId === result.user_id ? '#fca5a5' : '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: deletingUserId === result.user_id ? 'not-allowed' : 'pointer',
                            color: '#dc2626',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: deletingUserId === result.user_id ? 0.5 : 1
                          }}
                          title={t("results.deleteResult")}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {hasMore && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '1.5rem' 
          }}>
            <Button onClick={handleLoadMore} variant="outline">
              {t("results.showMore")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsTable;
